import React from "react";
import { ArrowLeft, Heart, User, Eye } from "lucide-react";
import type { UserWithLikes, UserLike } from "@/types";

interface UserLikesListProps {
  users: UserWithLikes[];
  onUserSelect: (userId: string) => void;
  isLoading: boolean;
}

interface UserLikesDetailProps {
  user: UserWithLikes | null;
  likes: UserLike[];
  onBack: () => void;
  isLoading: boolean;
}

/**
 * UserLikesList - Component for displaying users with likes in tabular format
 * Single Responsibility: Display users table and handle selection
 * Updated: Fixed module export issue
 */
export const UserLikesList: React.FC<UserLikesListProps> = ({
  users,
  onUserSelect,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="flex-1 text-center mt-20">
        Loading users with likes...
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <Heart size={48} />
        </div>
        <h3>No Users Found</h3>
        <p>No users with likes found in the system.</p>
      </div>
    );
  }

  return (
    <div className="users-table-container">
      <div className="table-header">
        <h3>Users with Likes</h3>
        <p>Total users: {users.length}</p>
      </div>

      <div className="table-wrapper">
        <table className="users-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Likes Count</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.user_id}>
                <td>{user.user_id}</td>
                <td>{user.Name}</td>
                <td>{user.Email}</td>
                <td>
                  <span className="likes-badge">{user.likes_count}</span>
                </td>
                <td>
                  <a
                    href={`?user_id=${encodeURIComponent(user.user_id)}`}
                    className="view-likes-btn"
                    title="View user's likes"
                    onClick={e => {
                      if (
                        e.button === 0 &&
                        !e.metaKey &&
                        !e.ctrlKey &&
                        !e.altKey &&
                        !e.shiftKey
                      ) {
                        e.preventDefault();
                        onUserSelect(user.user_id);
                      }
                      // Otherwise, let browser handle (right/middle/modifier)
                    }}
                  >
                    <Eye size={16} />
                    See User Likes
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * UserLikesDetail - Component for displaying individual user's likes
 * Single Responsibility: Display user details and their likes
 */
export const UserLikesDetail: React.FC<UserLikesDetailProps> = ({
  user,
  likes,
  onBack,
  isLoading,
}) => {
  if (!user) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <User size={48} />
        </div>
        <h3>User Not Found</h3>
        <p>The selected user could not be found.</p>
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={16} />
          Back to Users
        </button>
      </div>
    );
  }

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid date";
    }
  };

  if (isLoading) {
    return (
      <div className="user-likes-detail">
        <div className="detail-header">
          <button onClick={onBack} className="back-button">
            <ArrowLeft size={16} />
            Back to Users
          </button>
          <div className="user-meta">
            <h2>{user.Name}'s Likes</h2>
            <p>{user.Email}</p>
          </div>
        </div>
        <div className="flex-1 text-center mt-20">Loading user likes...</div>
      </div>
    );
  }

  return (
    <div className="user-likes-detail">
      <div className="detail-header">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={16} />
          Back to Users
        </button>
        <div className="user-meta">
          <h2>{user.Name}'s Likes</h2>
          <p>
            {user.Email} • {user.likes_count} total likes
          </p>
        </div>
      </div>

      {likes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Heart size={48} />
          </div>
          <h3>No Likes Found</h3>
          <p>{user.Name} hasn't liked any styles yet.</p>
        </div>
      ) : (
        <div className="likes-grid">
          {likes.map((like) => (
            <div key={like.like_id} className="like-card">
              {like.cover_image_url && (
                <div className="like-image">
                  <img
                    src={like.cover_image_url}
                    alt={like.name}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                    }}
                  />
                </div>
              )}
              <div className="like-info">
                <h4 className="like-style-name">{like.name}</h4>
                {/* <p className="like-style-id">Style ID: {like.style_id}</p> */}
                <p className="like-timestamp">
                  Liked on {formatTimestamp(like.created_at)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
